/**
 * SkinClarity Mobile Image Rotation
 * 
 * Automatically rotates between product images when cards are in viewport on mobile.
 * Uses IntersectionObserver for performance and respects prefers-reduced-motion.
 * 
 * Features:
 * - Only active on mobile devices (max-width: 768px)
 * - Uses single shared IntersectionObserver for performance
 * - Pauses when page is hidden (battery saving)
 * - Respects prefers-reduced-motion accessibility setting
 * - Smooth opacity transitions
 * - Configurable timing
 */

(function() {
  'use strict';

  class MobileImageRotator {
    constructor(options = {}) {
      this.options = {
        animationInterval: options.animationInterval || 2500, // Time between image switches
        mobileBreakpoint: options.mobileBreakpoint || '(max-width: 768px)',
        rootMargin: options.rootMargin || '50px 0px', // Pre-load slightly before visible
        threshold: options.threshold || 0.3, // 30% visible before activating
        containerSelector: options.containerSelector || '.product-card',
        imageContainerSelector: options.imageContainerSelector || '.product-image',
        mainImageSelector: options.mainImageSelector || '.main-image',
        secondImageSelector: options.secondImageSelector || '.second-image',
        ...options
      };

      this.observer = null;
      this.mediaQuery = null;
      this.reducedMotionQuery = null;
      this.animationTimers = new Map(); // card element -> timer ID
      this.isVisible = new Map(); // card element -> visibility state
      this.isMobileEnabled = false;
      this.pageHidden = false;
      this.visibilityHandler = null;

      this.init();
    }

    init() {
      // Feature detection
      if (!('IntersectionObserver' in window) || !window.matchMedia) {
        console.warn('[MobileImageRotator] IntersectionObserver or matchMedia not supported');
        return;
      }

      // Set up media query monitoring for mobile detection
      this.mediaQuery = window.matchMedia(this.options.mobileBreakpoint);
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      // Bind the handler so we can remove it later
      this.handleMediaQueryChange = this.handleMediaQueryChange.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

      this.mediaQuery.addEventListener('change', this.handleMediaQueryChange);

      // Initial check
      this.handleMediaQueryChange(this.mediaQuery);

      // Log initialization
      console.log('[MobileImageRotator] Initialized', {
        isMobile: this.mediaQuery.matches,
        reducedMotion: this.reducedMotionQuery.matches
      });
    }

    handleMediaQueryChange(mediaQueryList) {
      const matches = mediaQueryList.matches !== undefined ? mediaQueryList.matches : mediaQueryList;
      
      if (matches) {
        // Mobile viewport - enable image rotation
        if (!this.isMobileEnabled) {
          this.isMobileEnabled = true;
          this.createObserver();
          this.attachPageVisibilityListener();
          console.log('[MobileImageRotator] Enabled for mobile');
        }
      } else {
        // Desktop viewport - disable image rotation
        if (this.isMobileEnabled) {
          this.isMobileEnabled = false;
          this.disconnectObserver();
          this.removePageVisibilityListener();
          console.log('[MobileImageRotator] Disabled for desktop');
        }
      }
    }

    createObserver() {
      if (this.observer) return;

      const observerOptions = {
        root: null, // viewport
        rootMargin: this.options.rootMargin,
        threshold: this.options.threshold
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.handleIntersectionChange(entry);
        });
      }, observerOptions);

      // Observe all product cards on the page
      this.observeAllCards();
    }

    observeAllCards() {
      if (!this.observer) return;

      const cards = document.querySelectorAll(this.options.containerSelector);
      
      cards.forEach(card => {
        // Only observe cards that have a second image
        const imageContainer = card.querySelector(this.options.imageContainerSelector);
        const secondImage = imageContainer?.querySelector(this.options.secondImageSelector);
        
        if (secondImage) {
          this.observer.observe(card);
          this.isVisible.set(card, false);
        }
      });

      console.log(`[MobileImageRotator] Observing ${cards.length} product cards`);
    }

    handleIntersectionChange(entry) {
      const card = entry.target;
      const wasVisible = this.isVisible.get(card);
      const isNowVisible = entry.isIntersecting;

      // Only handle state changes
      if (wasVisible === isNowVisible) return;

      this.isVisible.set(card, isNowVisible);

      if (isNowVisible && !this.pageHidden) {
        // Card entered viewport - start animation
        this.startImageRotation(card);
      } else {
        // Card left viewport or page hidden - stop animation
        this.stopImageRotation(card);
      }
    }

    startImageRotation(card) {
      // Prevent multiple timers on same card
      if (this.animationTimers.has(card)) {
        return;
      }

      // Check for reduced motion preference
      if (this.prefersReducedMotion()) {
        return;
      }

      const imageContainer = card.querySelector(this.options.imageContainerSelector);
      if (!imageContainer) return;

      const mainImage = imageContainer.querySelector(this.options.mainImageSelector);
      const secondImage = imageContainer.querySelector(this.options.secondImageSelector);

      if (!mainImage || !secondImage) return;

      let showingSecond = false;

      const rotateImages = () => {
        showingSecond = !showingSecond;

        if (showingSecond) {
          // Show second image - use same classes as brand-showcase
          mainImage.classList.add('hide');
          secondImage.classList.add('show');
        } else {
          // Show first image
          mainImage.classList.remove('hide');
          secondImage.classList.remove('show');
        }
      };

      // Start with first rotation after a short delay
      const initialTimer = setTimeout(() => {
        rotateImages();
        
        // Set up repeating rotation
        const intervalId = setInterval(rotateImages, this.options.animationInterval);
        
        // Store the interval ID (overwriting the initial timer ID)
        this.animationTimers.set(card, intervalId);
      }, 800); // Initial delay before first switch

      // Store the initial timer
      this.animationTimers.set(card, initialTimer);
    }

    stopImageRotation(card) {
      const timerId = this.animationTimers.get(card);
      if (timerId !== undefined) {
        clearInterval(timerId);
        clearTimeout(timerId);
        this.animationTimers.delete(card);
      }

      // Reset to first image
      const imageContainer = card.querySelector(this.options.imageContainerSelector);
      if (imageContainer) {
        const mainImage = imageContainer.querySelector(this.options.mainImageSelector);
        const secondImage = imageContainer.querySelector(this.options.secondImageSelector);

        if (mainImage) mainImage.classList.remove('hide');
        if (secondImage) secondImage.classList.remove('show');
      }
    }

    prefersReducedMotion() {
      return this.reducedMotionQuery && this.reducedMotionQuery.matches;
    }

    handleVisibilityChange() {
      if (document.hidden) {
        // Page is hidden - pause all animations
        this.pageHidden = true;
        this.animationTimers.forEach((timerId, card) => {
          clearInterval(timerId);
          clearTimeout(timerId);
        });
        this.animationTimers.clear();
        console.log('[MobileImageRotator] Paused - page hidden');
      } else {
        // Page is visible - resume animations for visible cards
        this.pageHidden = false;
        this.isVisible.forEach((isVisible, card) => {
          if (isVisible && !this.animationTimers.has(card)) {
            this.startImageRotation(card);
          }
        });
        console.log('[MobileImageRotator] Resumed - page visible');
      }
    }

    attachPageVisibilityListener() {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    removePageVisibilityListener() {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      // Clear all running timers and reset images
      this.animationTimers.forEach((timerId, card) => {
        clearInterval(timerId);
        clearTimeout(timerId);
        
        // Reset images to default state
        const imageContainer = card.querySelector(this.options.imageContainerSelector);
        if (imageContainer) {
          const mainImage = imageContainer.querySelector(this.options.mainImageSelector);
          const secondImage = imageContainer.querySelector(this.options.secondImageSelector);
          if (mainImage) mainImage.classList.remove('hide');
          if (secondImage) secondImage.classList.remove('show');
        }
      });
      
      this.animationTimers.clear();
      this.isVisible.clear();
    }

    // Public method to re-observe (useful for dynamically added content)
    refresh() {
      if (this.observer && this.isMobileEnabled) {
        this.observeAllCards();
      }
    }

    // Clean up everything
    destroy() {
      this.disconnectObserver();
      this.removePageVisibilityListener();
      if (this.mediaQuery) {
        this.mediaQuery.removeEventListener('change', this.handleMediaQueryChange);
        this.mediaQuery = null;
      }
    }
  }

  // Initialize on DOM ready
  function initializeRotator() {
    // Delay slightly to ensure all sections are rendered
    window.scMobileImageRotator = new MobileImageRotator({
      animationInterval: 2500, // Switch every 2.5 seconds
      mobileBreakpoint: '(max-width: 768px)',
      rootMargin: '50px 0px',
      threshold: 0.3
    });

    // Re-observe when new content is loaded (e.g., infinite scroll, AJAX)
    document.addEventListener('shopify:section:load', () => {
      if (window.scMobileImageRotator) {
        window.scMobileImageRotator.refresh();
      }
    });
  }

  // Plan 08-01: idle-init for INP/TBT optimization.
  // Mobile image rotator is purely below-the-fold eye-candy — defer its boot
  // until the browser is idle so it doesn't compete with above-the-fold paint.
  function scheduleInit() {
    var runIdle = (typeof window !== 'undefined' && window.scRunOnIdle) ||
                  function(cb) { setTimeout(cb, 1500); };
    runIdle(initializeRotator, 2000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInit, { once: true });
  } else if (document.readyState === 'complete') {
    scheduleInit();
  } else {
    window.addEventListener('load', scheduleInit, { once: true, passive: true });
  }
})();
